import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Building2, ExternalLink, AlertCircle } from "lucide-react";

/**
 * Public preview of a tenant's branded landing card.
 *
 * Hits the unauthenticated `/api/public/v1/tenant/lookup` endpoint and renders
 * the workspace name, logo, and primary color exactly as a visitor of
 * `<subdomain>.<root>` would see them once wildcard DNS is wired up.
 */
export default function TenantPreview() {
  const { subdomain } = useParams();
  const [tenant, setTenant] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/v1/tenant/lookup?subdomain=${encodeURIComponent(subdomain)}`;
    axios
      .get(url)
      .then((r) => setTenant(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "Workspace not found." : "Lookup failed."));
  }, [subdomain]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="axia-card p-10 max-w-md text-center" data-testid="preview-error">
          <AlertCircle className="w-8 h-8 mx-auto text-rose-500 mb-3" />
          <div className="font-display text-xl font-semibold text-slate-900">{error}</div>
          <p className="text-sm text-slate-500 mt-2">
            <code className="font-mono">{subdomain}</code> isn't a registered Axia workspace.
          </p>
          <Link to="/login" className="inline-block mt-5 text-sm text-slate-700 underline">Go to sign-in</Link>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400">Loading…</div>;
  }

  const tint = tenant.primary_color || "#0F172A";

  return (
    <div className="min-h-screen flex" data-testid="tenant-preview-page">
      {/* Left: branded hero */}
      <aside className="hidden lg:flex flex-1 items-center justify-center p-12 text-white relative overflow-hidden" style={{ background: tint }}>
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.5), transparent 40%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.4), transparent 40%)",
          }}
        />
        <div className="relative z-10 max-w-md">
          {tenant.logo_url ? (
            <img src={tenant.logo_url} alt={tenant.name} className="w-20 h-20 rounded-xl bg-white/10 backdrop-blur p-3 mb-6" />
          ) : (
            <div className="w-20 h-20 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center mb-6 text-3xl font-bold">
              {(tenant.name || "?")[0].toUpperCase()}
            </div>
          )}
          <div className="text-xs uppercase tracking-[0.2em] opacity-80 mb-2">Powered by Axia</div>
          <h1 className="font-display text-4xl font-bold leading-tight">{tenant.name}</h1>
          <p className="text-base opacity-85 mt-3">
            Performance home for the <span className="font-semibold">{tenant.name}</span> team — goals, check-ins, reviews and recognition, all in one place.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 text-xs font-mono bg-white/10 backdrop-blur rounded-md px-3 py-2">
            <Building2 className="w-3.5 h-3.5" />
            {tenant.subdomain}
          </div>
        </div>
      </aside>

      {/* Right: sign-in nudge */}
      <main className="flex-1 flex items-center justify-center p-12 bg-white">
        <div className="max-w-sm w-full">
          <div className="axia-overline mb-2">Workspace preview</div>
          <h2 className="font-display text-2xl font-semibold text-slate-900">This is what {tenant.name} will look like.</h2>
          <p className="text-sm text-slate-500 mt-3">
            When wildcard DNS is configured, anyone who visits{" "}
            <code className="font-mono text-slate-700">{tenant.subdomain}.&lt;your-root&gt;</code> will land here, with the workspace branding loaded before sign-in.
          </p>
          <Link
            to="/login"
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-sm font-medium hover:opacity-90 transition"
            style={{ background: tint }}
            data-testid="preview-signin-link"
          >
            Continue to sign-in <ExternalLink className="w-3 h-3" />
          </Link>

          <div className="mt-10 rounded-lg border border-slate-200 p-4 bg-slate-50/60">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Brand details</div>
            <dl className="mt-2 space-y-1 text-xs text-slate-700">
              <div className="flex justify-between"><dt className="text-slate-500">Subdomain</dt><dd className="font-mono">{tenant.subdomain}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Primary color</dt><dd className="font-mono">{tint}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Tenant ID</dt><dd className="font-mono">{tenant.id?.slice(0, 8)}…</dd></div>
            </dl>
          </div>
        </div>
      </main>
    </div>
  );
}
